import { defineComponent, resolveComponent, nextTick } from 'vue';
import { QBtn, QInput } from 'quasar';
import utils from '../../Shared/utils.jsx';
import methods from '../../Shared/methods';

export function findTextInModel(cmodel, findText, casesensitive, occurrence) {
    if (!cmodel) return null;

    if (Array.isArray(cmodel))
        for (let i = 0; i < cmodel.length; i++) {
            const node = cmodel[i];
            if (typeof node === 'object') {
                const found = findTextInModel(node, findText, casesensitive, occurrence);
                if (found)
                    return found;
            }

            if (typeof node === 'string')
                if (((casesensitive && node.includes(findText)) || (!casesensitive && node.toLowerCase().includes(findText))) && --occurrence.value <= 0)
                    return cmodel;
        }
    else
        for (let key in cmodel) {
            if (key.substr(0, 1) === '$') continue;
            const node = cmodel[key];

            if (((casesensitive && key.includes(findText)) || (!casesensitive && key.toLowerCase().includes(findText))) && --occurrence.value <= 0)
                return cmodel;

            if (typeof node === 'object') {
                const found = findTextInModel(node, findText, casesensitive, occurrence);
                if (found)
                    return found;
            }

            if (typeof node === 'string')
                if (((casesensitive && node.includes(findText)) || (!casesensitive && node.toLowerCase().includes(findText))) && --occurrence.value <= 0)
                    return cmodel;
        }

    return null;
}

const TreeLabel = defineComponent({
    name: 'document-tree-label',
    data: () => ({
        title: null,
        titletext: null,
        schema: null,
        color: null,
    }),
    created() {
        if (this.cmodel && '$typeSchema' in this.cmodel)
            this.$watch('cmodel.$typeSchema', function () { if (this.typeSchema) this.Refresh(); });
    },
    mounted() {
        if (this.typeSchema)
            this.Refresh();
    },
    computed: {
        ControlData: function () {
            return this.cmodel.ControlData;
        },
        ActionData: function () {
            return this.cmodel.ActionData;
        },
        ActionExpression: function () {
            return this.cmodel.ActionExpression;
        },
        Field: function () {
            return this.cmodel.Field;
        },
        Name: function () {
            return this.cmodel.Name;
        },
        StateName: function () {
            return this.cmodel.StateName;
        },
        EventName: function () {
            return this.cmodel.EventName;
        },
        NextStatePath: function () {
            return this.cmodel.NextStatePath;
        },
    },
    methods: {
        ...methods,
        async Refresh() {
            try {
                this.schema = await utils.schema.get(this.typeSchema);
                const res = utils.schema.resolve_Of(this.schema);

                this.titletext = res.title;

                if (res.documentEditorTitle)
                    this.title = utils.compile(this, res.documentEditorTitle);
                else
                    this.title = null;
            }
            catch (e) {
                utils.warn('Error resolving documentEditorTitle in DocumentTreePanel with typeSchema:' + this.typeSchema + '; ' + e);
                this.title = '';
            }
        }
    },
    props: {
        label: '',
        text: '',
        typeSchema: null,
        cmodel: null,
        parent: null,
    },
    render() {
        let title = this.titletext || this.text || '';
        if (this.title)
            try {
                title = utils.evaluate(this.title, this.cmodel);
            }
            catch (e) {
                utils.warn('DocumentTreePanel title failed to evaluate title: ' + e);
            }

        let label = this.label;
        const suffix = title || (this.schema ? this.schema.title : '');
        if (suffix)
            label += ': ' + suffix;

        let highlight;
        if (this.cmodel.ActionExpression && (typeof this.cmodel.ActionExpression === 'string') && this.cmodel.ActionExpression.startsWith('false'))
            highlight = true;

        let permissions;
        if (this.cmodel.AccessPermission && this.cmodel.AccessPermission.Enabled)
            permissions = true;

        const classNames = {
            'treeview-node': true,
            selected: this.parent.SelectedNode == this.cmodel,
            highlight: highlight,
            permissions: permissions,
            hastype: '$typeSchema' in this.cmodel && !highlight,
            hasnotype: !('$typeSchema' in this.cmodel) && !highlight,
        };

        let styles;
        if (this.color)
            styles = { color: this.color };

        return <span class={classNames} style={styles} onClick={() => this.parent.SelectNode(this.cmodel)}> {label}</span>;
    }
});

const TreeNode = defineComponent({
    name: 'document-tree-node',
    components: {
        TreeLabel,
    },
    props: {
        name: '',
        root: null,
        parent: null,
        cmodel: null,
        level: 0,
        pathSegments: { type: Array, default: () => [] },
        treeName: { type: String, default: 'Tree' },
    },
    render() {
        if (!this.cmodel)
            return null;

        const TreeNode = resolveComponent('document-tree-node');

        const elements = [];
        if (Array.isArray(this.cmodel))
            for (let i = 0; i < this.cmodel.length; i++) {
                const node = this.cmodel[i];

                if (typeof node === 'object') {
                    const childPath = this.pathSegments.concat(i);
                    const vis = this.parent.isTreeExpanded(childPath, this.level);
                    const visctl = vis ? <i class="mdi mdi-minus-box-outline"></i> : <i class="mdi mdi-plus-box"></i>;

                    elements.push(
                        <div style={{ whiteSpace: "nowrap" }}>
                            <span class="treeview-expand-collapse" onClick={() => this.parent.toggleTreeExpanded(childPath, this.level)}>{visctl}</span>
                            <TreeLabel label={'(' + i + ')'} typeSchema={node.$typeSchema} parent={this.parent} cmodel={node}></TreeLabel>
                        </div>
                    );

                    if (vis)
                        elements.push(<TreeNode name={i} root={this.root} parent={this.parent} cmodel={node} level={this.level + 1} pathSegments={childPath} treeName={this.treeName}></TreeNode>);
                }
            }
        else
            for (let key in this.cmodel) {
                if (key.substr(0, 1) === '$') continue;
                const node = this.cmodel[key];

                if (node && typeof node === 'object') {
                    const childPath = this.pathSegments.concat(key);
                    const vis = this.parent.isTreeExpanded(childPath, this.level);
                    const visctl = vis ? <i class="mdi mdi-minus-box-outline"></i> : <i class="mdi mdi-plus-box"></i>;

                    elements.push(
                        <div style={{ whiteSpace: "nowrap" }}>
                            <span class="treeview-expand-collapse" onClick={() => this.parent.toggleTreeExpanded(childPath, this.level)}>{visctl}</span>
                            <TreeLabel label={key} typeSchema={node.$typeSchema} parent={this.parent} cmodel={node}></TreeLabel>
                        </div>
                    );
                    if (vis)
                        elements.push(<TreeNode name={key} root={this.root} parent={this.parent} cmodel={node} level={this.level + 1} pathSegments={childPath} treeName={this.treeName}></TreeNode>);
                }
            }

        return (
            <div style={{ marginLeft: "15px" }}>
                {elements}
            </div>
        );
    }
});

export default defineComponent({
    name: 'document-tree-panel',
    components: {
        QBtn,
        QInput,
        TreeLabel,
        TreeNode,
    },
    emits: ['update:selectedNode', 'navigateBack'],
    props: {
        model: { type: Object, default: null },
        title: { type: String, default: '' },
        width: { type: Number, default: null },
        history: { type: Array, default: () => [] },
        treeName: { type: String, default: 'Tree' },
        root: { default: null },
        className: { type: String, default: '' },
        outerStyle: { type: Object, default: null },
    },
    data: () => ({
        findText: '',
        findOccurrence: 1,
        findCaseSensitive: false,
        expansionOverrides: {},
        internalSelectedNode: null,
    }),
    computed: {
        SelectedNode() {
            return this.internalSelectedNode;
        },
        panelOuterStyle() {
            return this.outerStyle || null;
        },
        panelWidthStyle() {
            if (this.width == null)
                return null;
            const w = parseInt(this.width, 10) || 300;
            return {
                minWidth: w + 'px',
                maxWidth: w + 'px',
            };
        },
    },
    methods: {
        treePathKey(segments) {
            return JSON.stringify(segments);
        },
        clearExpansionState() {
            this.expansionOverrides = {};
        },
        isTreeExpanded(childSegments, parentLevel) {
            const key = this.treePathKey(childSegments);
            if (Object.prototype.hasOwnProperty.call(this.expansionOverrides, key))
                return this.expansionOverrides[key];
            return parentLevel <= 0;
        },
        toggleTreeExpanded(childSegments, parentLevel) {
            const key = this.treePathKey(childSegments);
            const cur = this.isTreeExpanded(childSegments, parentLevel);
            const next = !cur;
            const def = parentLevel <= 0;
            const nextOverrides = { ...this.expansionOverrides };
            if (next === def)
                delete nextOverrides[key];
            else
                nextOverrides[key] = next;
            this.expansionOverrides = nextOverrides;
        },
        expandToNode(node) {
            const path = [];
            if (utils.findNodePath(path, this.model, node)) {
                const segments = [];
                const nextOverrides = { ...this.expansionOverrides };
                for (let i = 0; i < path.length - 1; i++) {
                    const item = path[i];
                    const seg = 'name' in item ? item.name : item.index;
                    segments.push(seg);
                    nextOverrides[this.treePathKey(segments)] = true;
                }
                this.expansionOverrides = nextOverrides;
                return path;
            }
            return null;
        },
        setSelectedNode(node, expandto, scrollIntoView) {
            if (!expandto && node === this.internalSelectedNode)
                return null;

            let path;
            if (expandto)
                path = this.expandToNode(node);
            this.internalSelectedNode = node;

            if (scrollIntoView && node)
                this.scrollSelectedIntoView();

            return path;
        },
        SelectNode(node, expandto, scrollIntoView) {
            if (!expandto && node === this.internalSelectedNode)
                return null;

            const path = this.setSelectedNode(node, expandto, scrollIntoView);
            this.$emit('update:selectedNode', node);
            return path;
        },
        scrollSelectedIntoView() {
            const bottomPad = 64;

            nextTick(() => {
                nextTick(() => {
                    const container = this._treeScrollEl;
                    if (!container)
                        return;

                    const selected = container.querySelector('.treeview-node.selected');
                    if (!selected)
                        return;

                    selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });

                    const selectedRect = selected.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const gapBelow = containerRect.bottom - selectedRect.bottom;

                    if (gapBelow >= bottomPad)
                        return;

                    const maxScroll = container.scrollHeight - container.clientHeight;
                    const extra = bottomPad - gapBelow;
                    const nextScroll = Math.min(container.scrollTop + extra, maxScroll);

                    if (nextScroll > container.scrollTop)
                        container.scrollTop = nextScroll;
                });
            });
        },
        setFindText(value) {
            if (this.findText !== value) {
                this.findText = value;
                this.findOccurrence = 0;
            }
        },
        findInputKeydown(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.find(e, this.findText);
            }
        },
        find(e, value) {
            this.findOccurrence++;

            const v = this.findCaseSensitive ? value : value.toLowerCase();
            const node = findTextInModel(this.model, v, this.findCaseSensitive, { value: this.findOccurrence });
            if (node) {
                const path = this.SelectNode(node, true);
                for (let i = path.length - 1; i >= 0; i--) {
                    const item = path[i];
                    if ('$typeSchema' in item.node) {
                        if (item.node != node)
                            this.SelectNode(item.node, false);
                        break;
                    }
                }
            }
            else {
                this.findOccurrence = 0;
                this.SelectNode(null, false);
            }
            if (node)
                this.scrollSelectedIntoView();
        },
    },
    render() {
        if (!this.model)
            return null;

        let style = {
            borderStyle: 'solid',
            borderWidth: '1px',
            display: "flex",
            flexDirection: "column",
            flexGrow: "0",
            flexShrink: "1",
            fontSize: "14px",
            overflow: "hidden",
        };
        if (this.panelOuterStyle)
            Object.assign(style, this.panelOuterStyle);
        if (this.panelWidthStyle)
            Object.assign(style, this.panelWidthStyle);

        let historybtn;
        if (this.history.length > 0)
            historybtn = (
                <QBtn flat size="sm" color="primary" title={'Back to ' + this.history[this.history.length - 1]} onClick={() => this.$emit('navigateBack')} icon="mdi-arrow-left-circle" />
            );

        return (
            <div
                class={{ 'c-DocumentTreePanel': true, [this.className]: !!this.className }}
                style={style}>
                <div class="property-grid" style={{ overflow: "hidden", padding: "0px", display: "flex", flexDirection: "column", flexGrow: "1", minHeight: "0" }}>
                    <div class="c-TreeView-title" style={{ fontWeight: "bold", fontSize: "14px", padding: "4px 0", margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>{historybtn} {this.title}</div>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "start", gap: "0" }}>
                        <QInput filled dense hide-bottom-space clearable placeholder="Enter Search Text" modelValue={this.findText} onUpdate:modelValue={(value) => this.setFindText(value)} onKeydown={this.findInputKeydown} style={{ width: '100%' }} />
                        <QBtn flat size="sm" class="q-px-sm" title="Search (Enter)" color="primary" icon="mdi-magnify" onClick={(e) => this.find(e, this.findText)} />
                        <QBtn flat={!this.findCaseSensitive} class="q-px-sm" outline={this.findCaseSensitive} size="sm" title="Match Case" color="primary" icon="mdi-format-size" onClick={(e) => this.findCaseSensitive = !this.findCaseSensitive} />
                    </div>
                    <div
                        ref={(el) => { this._treeScrollEl = el; }}
                        style={{ display: "flex", flexDirection: "column", overflow: "auto", marginTop: "6px", flexGrow: "1" }}>
                        <div style={{ whiteSpace: "nowrap" }}>
                            <TreeLabel label="Root" typeSchema={this.model.$typeSchema} parent={this} cmodel={this.model}></TreeLabel>
                        </div>
                        <TreeNode name={this.treeName} root={this.root} parent={this} cmodel={this.model} level={0} pathSegments={[]} treeName={this.treeName}></TreeNode>
                    </div>
                </div>
            </div>
        );
    },
});
